import { memo, useCallback } from 'react';

import {
  BulkEditMultiSelectCustomFieldModal,
  type BulkEditMultiSelectCustomFieldModalProps,
} from '~/components/Modals/BulkEditCustomFieldModal/BulkEditMultiSelectCustomFieldModal';
import {
  type ResolveInitialValueFn,
  useSelectAllCustomFieldModal,
} from '~/hooks/customFields/useSelectAllCustomFieldModal';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useGetPrivateCustomFieldOptions } from '~/swr-hooks/customFields/useGetPrivateCustomFieldOptions';
import { useUpdateItemsCustomFieldValue } from '~/utils/CustomFields/useUpdateItemsCustomFieldValue';

export type PrivateBulkEditMultiSelectCustomFieldModalProps = Omit<
  BulkEditMultiSelectCustomFieldModalProps,
  'onSave' | 'cfOptions'
>;

const resolveInitialValue: ResolveInitialValueFn = (common, field, cfOptions) => {
  const valueIds = common.value && 'valueIds' in common.value ? common.value.valueIds : [];
  const resolvedValues =
    cfOptions?.filter((option) => valueIds.includes(option.id)) ||
    field.values?.filter((option) => valueIds.includes(option.id)) ||
    [];

  return {
    ...field,
    value: null,
    values: resolvedValues,
    plainTextValue: undefined,
    dateValue: undefined,
  };
};

export const PrivateBulkEditMultiSelectCustomFieldModal = memo(
  (props: AirModalProps<PrivateBulkEditMultiSelectCustomFieldModalProps>) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const workspaceId = currentWorkspace?.id;
    const { updateItemsCustomFieldValue } = useUpdateItemsCustomFieldValue();

    const { data: cfOptions } = useGetPrivateCustomFieldOptions({
      customFieldId: props.field.id,
    });

    const { selectAll, initialValues, initialStatus, isLoadingInitialValues, handleSelectAllSave } =
      useSelectAllCustomFieldModal({
        field: props.field,
        fallbackInitialValues: props.initialValues,
        resolveInitialValue,
        cfOptions: cfOptions?.data,
      });

    const onSave = useCallback<BulkEditMultiSelectCustomFieldModalProps['onSave']>(
      async (params) => {
        if (selectAll) {
          await handleSelectAllSave({
            baseCustomField: params.baseCustomField,
            operation: 'multiSelect',
            trackLocation: 'context-menu-bulk-edit',
            valueIdsToAdd: params.multiValuesToAdd.map((value) => value.id),
            valueIdsToRemove: params.multiValueIdsToRemove,
          });
          return;
        }

        await updateItemsCustomFieldValue({
          ...params,
          trackingLocation: 'context-menu-bulk-edit',
          workspaceId,
        });
      },
      [handleSelectAllSave, selectAll, updateItemsCustomFieldValue, workspaceId],
    );

    return (
      <BulkEditMultiSelectCustomFieldModal
        {...props}
        initialValues={initialValues}
        initialStatus={initialStatus}
        isLoadingInitialValues={isLoadingInitialValues}
        cfOptions={cfOptions}
        onSave={onSave}
      />
    );
  },
);
PrivateBulkEditMultiSelectCustomFieldModal.displayName = 'PrivateBulkEditMultiSelectCustomFieldModal';
